import { useEffect, useSyncExternalStore } from 'react';

/**
 * Os avisos que abrem sozinhos ao entrar no painel, por prioridade (menor abre primeiro).
 *
 * No máximo um deles abre por carregamento de página: dois modais seguidos, ou um sobre o outro,
 * são fechados sem leitura. O que perde a vez espera o próximo carregamento, quando o que abriu
 * já foi visto e não volta (cada aviso guarda o próprio "visto").
 *
 * `foraDoCarregamento`: o tour de uma tela não é aviso de entrada. Ele só não abre por cima de um
 * aviso, nem deixa um aviso abrir por cima dele; fechado um, o outro tem a vez. Se gastasse o
 * carregamento, quem viu o impedimento no início e foi para Produtos ficaria sem o tour até
 * recarregar, e o tour aberto empurraria o impedimento para o dia seguinte.
 */
export const AVISO = {
  impedimentoNfse: { id: 'impedimento-nfse', prioridade: 10 },
  recusaNotaAvulsa: { id: 'recusa-nota-avulsa', prioridade: 20 },
  tourProdutos: { id: 'tour-produtos-v2', prioridade: 30, foraDoCarregamento: true },
};

// Estado de módulo, e não de contexto: os avisos moram em pontos diferentes do layout, e o
// módulo vive exatamente um carregamento de página.
let registros = new Map();
// Quem está na tela agora. É liberado quando ele fecha, para o tour (ou o aviso que o tour
// segurava) ter a vez; entre os avisos de entrada, quem segura o carregamento é `doCarregamento`.
let dono = null;
let doCarregamento = null;
let versao = 0;
const ouvintes = new Set();

const notificar = () => {
  versao += 1;
  ouvintes.forEach((ouvinte) => ouvinte());
};

const assinar = (ouvinte) => {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
};

const lerVersao = () => versao;

const registrar = (id, estado) => {
  const atual = registros.get(id);
  if (
    atual &&
    atual.prioridade === estado.prioridade &&
    atual.foraDoCarregamento === estado.foraDoCarregamento &&
    atual.decidido === estado.decidido &&
    atual.querAbrir === estado.querAbrir
  ) {
    return;
  }
  registros.set(id, estado);
  if (dono === id && !estado.querAbrir) dono = null;
  notificar();
};

const remover = (id) => {
  if (dono === id) dono = null;
  if (registros.delete(id)) notificar();
};

// O aviso de entrada que já abriu pode voltar (o modal de novidades o escondeu, por exemplo); os
// outros esperam o próximo carregamento.
const perdeuOCarregamento = (id, estado) =>
  !estado.foraDoCarregamento && doCarregamento !== null && doCarregamento !== id;

const tomarAVez = (id) => {
  if (dono) return;
  dono = id;
  if (!registros.get(id)?.foraDoCarregamento) doCarregamento = id;
  notificar();
};

/**
 * Pode abrir quem quer abrir, se nenhum aviso de prioridade maior ainda está decidindo ou também
 * quer abrir (quem está na tela é conferido em `tomarAVez`). Esperar o que ainda decide é o que dá a vez
 * ao de maior prioridade mesmo quando a consulta dele volta depois.
 */
const podeAbrir = (id, prioridade, querAbrir) => {
  if (!querAbrir) return false;
  // Só depois de registrado: os avisos montados juntos se registram no mesmo commit, e esperar o
  // próprio registro é o que garante que o de prioridade maior já está na conta.
  const proprio = registros.get(id);
  if (!proprio || perdeuOCarregamento(id, proprio)) return false;

  // Quem perdeu o carregamento não abre mais nele; esperar por ele seria esperar para sempre.
  return [...registros.entries()].every(
    ([outro, estado]) =>
      outro === id ||
      estado.prioridade >= prioridade ||
      (estado.decidido && !estado.querAbrir) ||
      perdeuOCarregamento(outro, estado)
  );
};

/**
 * Registra o aviso no coordenador e diz se ele pode abrir agora.
 *
 * `decidido`: o aviso já sabe se quer abrir (a consulta dele voltou, ou ele não se aplica).
 * `querAbrir`: tem o que mostrar, nesta tela, agora.
 */
export function useAvisoUnico({ id, prioridade, foraDoCarregamento }, { decidido, querAbrir }) {
  useSyncExternalStore(assinar, lerVersao, lerVersao);

  useEffect(() => {
    registrar(id, {
      prioridade,
      foraDoCarregamento: Boolean(foraDoCarregamento),
      decidido: Boolean(decidido),
      querAbrir: Boolean(querAbrir),
    });
  }, [id, prioridade, foraDoCarregamento, decidido, querAbrir]);

  useEffect(() => () => remover(id), [id]);

  const candidato = !dono && podeAbrir(id, prioridade, Boolean(querAbrir));

  // A vez é tomada depois do commit, e não no render: dois avisos que passam a querer abrir no
  // mesmo commit (o modal de novidades fechou, por exemplo) leriam no render o registro de antes
  // e abririam juntos. Depois do commit, todos já se registraram, e a conta é refeita.
  useEffect(() => {
    if (!candidato) return undefined;

    const timer = setTimeout(() => {
      const atual = registros.get(id);
      if (atual && podeAbrir(id, atual.prioridade, atual.querAbrir)) tomarAVez(id);
    });

    return () => clearTimeout(timer);
  }, [candidato, id]);

  return dono === id && Boolean(querAbrir);
}

/** Um carregamento de página novo. Só para testes: no app, recarregar a página faz isso. */
export function reiniciarAvisoUnico() {
  registros = new Map();
  dono = null;
  doCarregamento = null;
  notificar();
}
